import { GUID, OpportunityView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Toast } from 'widgets';

import { useDeleteOpportunity, useOpportunity } from '../opportunity-queries';

export interface DeleteOpportunityDialogProps {
    opportunityGuid: GUID<OpportunityView>;
    open?: boolean;
    onOpenChange: (open: boolean) => void;
    onCancel?: () => void;
}

export const DeleteOpportunityDialog = (props: DeleteOpportunityDialogProps) => {
    const deleteMutation = useDeleteOpportunity();
    const { data: opportunity, isLoading } = useOpportunity(props.opportunityGuid);

    const handleDelete = () => {
        if (!props.opportunityGuid) return;

        deleteMutation
            .mutateAsync(props.opportunityGuid)
            .then(() => {
                Toast.success(`Opportunity "${opportunity?.name?.slice(0, 20) || 'Untitled'}" deleted successfully`);
                props.onOpenChange(false);
            })
            .catch(() => {
                Toast.forError(
                    new Error(`Failed to delete opportunity "${opportunity?.name?.slice(0, 20) || 'Untitled'}"`),
                    'Failed to delete opportunity',
                );
            });
    };

    if (isLoading) {
        return (
            <Dialog open={props.open} onOpenChange={props.onOpenChange}>
                <DialogContent className="overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Delete Opportunity</DialogTitle>
                        <DialogDescription>Loading opportunity details...</DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </Dialog>
        );
    }

    if (!opportunity) {
        return (
            <Dialog open={props.open} onOpenChange={props.onOpenChange}>
                <DialogContent className="overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Delete Opportunity</DialogTitle>
                        <DialogDescription>Opportunity not found.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={props.onCancel ?? (() => props.onOpenChange(false))}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={props.open} onOpenChange={props.onOpenChange}>
            <DialogContent className="overflow-hidden">
                <DialogHeader>
                    <DialogTitle>Delete Opportunity</DialogTitle>
                    <DialogDescription>Are you sure you want to delete this opportunity?</DialogDescription>
                    <div className="mt-2">
                        <p className="max-w-[300px] truncate font-semibold">
                            {opportunity.name || 'Untitled Opportunity'}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm">This action cannot be undone.</p>
                    </div>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={props.onCancel ?? (() => props.onOpenChange(false))}
                        disabled={deleteMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={() => handleDelete()} disabled={deleteMutation.isPending}>
                        {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default DeleteOpportunityDialog;
